import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { usePermissionCheck } from '../../contexts/PermissionContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { getDocumentSecurityAccess } from '../../config/documentSecurity'
import apiService from '../../services/api'
import { tokenStorage } from '../../utils/crypto'
import {
  HiOutlineArrowLeft,
  HiOutlineDocumentText,
  HiOutlineEye,
  HiOutlineArrowDownTray,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineShieldCheck,
  HiOutlineShieldExclamation,
  HiOutlineExclamationTriangle,
  HiOutlineUser,
  HiOutlineFolder,
  HiOutlineCalendar,
  HiOutlineClock,
  HiOutlineCloudArrowUp,
  HiOutlineXMark,
  HiOutlineMagnifyingGlass,
  HiOutlineChevronRight,
} from 'react-icons/hi2'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import StrandedFilesBanner from '../../components/documents/StrandedFilesBanner'
import DocumentComments from '../../components/documents/DocumentComments'
import DocumentSeenByList from '../../components/documents/DocumentSeenByList'
import { documentAPI } from '../../services/api'
import {
  Badge,
  Banner,
  Button,
  Card,
  EmptyState,
  FormField,
  IconButton,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
} from '../../components/ui'

const SECURITY_LEVELS = {
  red:    { tone: 'danger',  icon: HiOutlineShieldExclamation,  labelKey: 'high' },
  yellow: { tone: 'warning', icon: HiOutlineExclamationTriangle, labelKey: 'medium' },
  green:  { tone: 'success', icon: HiOutlineShieldCheck,        labelKey: 'low' },
}

function SecurityBadge({ level }) {
  const { t } = useTranslation()
  const cfg = SECURITY_LEVELS[level] || SECURITY_LEVELS.green
  const Icon = cfg.icon
  return (
    <Badge tone={cfg.tone}>
      <Icon className="w-4 h-4" />
      {t(`documents.security.${cfg.labelKey}`)}
    </Badge>
  )
}

function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 Bytes'
  if (isNaN(bytes) || bytes < 0) return 'Unknown size'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  if (i >= sizes.length) return 'Size too large'
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

function formatDateTime(s) {
  return new Date(s).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

export default function DocumentDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { isWorkspaceSwitched, ownerAccountId, user } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const { canUpdate, canDelete, hasPermission, userRole } = usePermissionCheck()
  const { getEntity } = useWorkspace()

  const [doc, setDoc] = useState(null)
  const [loading, setLoading] = useState(true)
  const [availableGroups, setAvailableGroups] = useState([])
  const [showMoveModal, setShowMoveModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editForm, setEditForm] = useState({ title: '', security_level: 'green' })
  const [showGroupDetailModal, setShowGroupDetailModal] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [moveGroupSearch, setMoveGroupSearch] = useState('')
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, title: '', message: '', onConfirm: null, type: 'warning',
  })

  const permissions = {
    canView:       hasPermission('document', 'read'),
    canEdit:       canUpdate('document'),
    canDelete:     canDelete('document'),
    canMoveGroups: canUpdate('document'),
  }

  const getDocumentSecurityAccessForRole = (documentSecurityLevel) => {
    const role = userRole?.toLowerCase() || 'silver'
    return getDocumentSecurityAccess(role, documentSecurityLevel, permissions)
  }

  useEffect(() => {
    fetchDocument()
    if (permissions.canMoveGroups) fetchAvailableGroups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isWorkspaceSwitched, ownerAccountId])

  async function fetchDocument() {
    try {
      setLoading(true)
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.get(`/documents/${id}/`, { params })
      if (response.success) {
        const data = response.data.data || response.data
        setDoc(data)
        setEditForm({ title: data.title || '', security_level: data.security_level || 'green' })
      } else {
        showError(response.error?.message || t('cases.loadFailed', { entity: getEntity('document').singular.toLowerCase() }))
        navigate('/documents')
      }
    } catch {
      showError(t('cases.loadFailed', { entity: getEntity('document').singular.toLowerCase() }))
      navigate('/documents')
    } finally {
      setLoading(false)
    }
  }

  async function fetchAvailableGroups() {
    try {
      const params = { page: 'all' }
      if (isWorkspaceSwitched && ownerAccountId) params.owner_account_id = ownerAccountId
      const response = await apiService.get('/document-groups/', { params })
      if (response.success) {
        const data = response.data
        setAvailableGroups(Array.isArray(data) ? data : (data.data || data.results || []))
      }
    } catch { /* swallow */ }
  }

  async function handleViewDocument() {
    try {
      if (!doc?.secure_view_url) { showError(t('documents.viewFailed')); return }
      const token = tokenStorage.getToken()
      if (!token) { showError(t('documents.viewFailed')); return }
      const response = await fetch(doc.secure_view_url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/pdf' },
        credentials: 'include',
      })
      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        window.open(url, '_blank')
        setTimeout(() => window.URL.revokeObjectURL(url), 1000)
        // Record an explicit "viewed" signal for the seen-by list (best-effort).
        documentAPI.recordSeen(id, 'view').catch(() => {})
      } else {
        showError(t('documents.viewFailed'))
      }
    } catch {
      showError(t('documents.viewFailed'))
    }
  }

  async function handleDownloadDocument() {
    try {
      if (!doc?.secure_download_url) { showError(t('documents.downloadFailed')); return }
      const token = tokenStorage.getToken()
      if (!token) { showError(t('documents.downloadFailed')); return }
      const response = await fetch(doc.secure_download_url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      })
      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const link = window.document.createElement('a')
        link.href = url; link.download = doc.file_name || 'document'
        window.document.body.appendChild(link); link.click(); window.document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        showSuccess(t('documents.downloadSuccess'))
        // Record an explicit "downloaded" signal for the seen-by list (best-effort).
        documentAPI.recordSeen(id, 'download').catch(() => {})
      } else {
        showError(t('documents.downloadFailed'))
      }
    } catch {
      showError(t('documents.downloadFailed'))
    }
  }

  async function handleDeleteDocument() {
    try {
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.delete(`/documents/${id}/`, { params })
      if (response.success) {
        showSuccess(t('documents.deleteSuccess', { entity: getEntity('document').singular }))
        navigate('/documents')
      } else {
        showError(response.error?.message || t('documents.deletedFailed', { entity: getEntity('document').singular.toLowerCase() }))
      }
    } catch {
      showError(t('documents.deletedFailed', { entity: getEntity('document').singular.toLowerCase() }))
    }
  }

  async function handleUpdateDocument() {
    try {
      if (!permissions.canEdit) {
        showError(t('common.permissionDenied'))
        return
      }
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.patch(`/documents/${id}/`, editForm, { params })
      if (response.success) {
        showSuccess(t('documents.updateSuccess', { entity: getEntity('document').singular }))
        setShowEditModal(false)
        fetchDocument()
      } else {
        showError(response.error?.message || t('documents.updateFailed', { entity: getEntity('document').singular.toLowerCase() }))
      }
    } catch {
      showError(t('documents.updateFailed', { entity: getEntity('document').singular.toLowerCase() }))
    }
  }

  async function handleMoveToGroup(groupId) {
    try {
      if (!permissions.canMoveGroups) {
        showError(t('common.permissionDenied'))
        return
      }
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.patch(
        `/documents/${id}/move-to-group/`,
        { group_id: groupId === 'ungrouped' ? null : parseInt(groupId) },
        { params },
      )
      if (response.success) {
        showSuccess(t('documents.moveSuccess', { entity: getEntity('document').singular }))
        setShowMoveModal(false)
        fetchDocument()
      } else {
        showError(response.error?.message || t('documents.moveFailed', { entity: getEntity('document').singular.toLowerCase() }))
      }
    } catch {
      showError(t('documents.moveFailed', { entity: getEntity('document').singular.toLowerCase() }))
    }
  }

  async function handleViewGroup(groupId) {
    if (!groupId) { showError(t('cases.loadFailed', { entity: `${getEntity('document').singular.toLowerCase()} group` })); return }
    try {
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.get(`/document-groups/${groupId}/`, { params })
      if (response.success) {
        setSelectedGroup(response.data.data || response.data)
        setShowGroupDetailModal(true)
      } else {
        showError(t('cases.loadFailed', { entity: `${getEntity('document').singular.toLowerCase()} group` }))
      }
    } catch {
      showError(t('cases.loadFailed', { entity: `${getEntity('document').singular.toLowerCase()} group` }))
    }
  }

  const confirmDeleteDocument = () => {
    setConfirmDialog({
      isOpen: true,
      title: t('documents.deleteTitle', { entity: getEntity('document').singular }),
      message: t('documents.deleteMessage', { name: doc?.file_name || doc?.title || '—' }),
      type: 'danger',
      onConfirm: () => {
        handleDeleteDocument()
        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
      },
    })
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-muted rounded-lg animate-pulse" />
          <div className="h-7 bg-muted rounded w-1/3 animate-pulse" />
        </div>
        <Card padding="md"><div className="h-64 bg-muted rounded-lg animate-pulse" /></Card>
        <div className="flex justify-center"><Spinner /></div>
      </div>
    )
  }

  if (!doc) {
    return (
      <Card padding="lg">
        <EmptyState
          icon={HiOutlineDocumentText}
          title={t('documents.detail.notFound', { entity: getEntity('document').singular })}
          description={t('documents.detail.notFoundDescription', { entity: getEntity('document').singular.toLowerCase() })}
          action={
            <Button leadingIcon={HiOutlineArrowLeft} onClick={() => navigate('/documents')}>
              {t('documents.detail.backTo', { entity: getEntity('document').plural })}
            </Button>
          }
        />
      </Card>
    )
  }

  const securityAccess = getDocumentSecurityAccessForRole(doc.security_level)

  return (
    <div className="space-y-6">
      <StrandedFilesBanner />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <IconButton
            icon={HiOutlineArrowLeft}
            aria-label={t('documents.detail.backTo', { entity: getEntity('document').plural })}
            variant="ghost"
            onClick={() => navigate('/documents')}
            className="rtl:rotate-180"
          />
          <span className="w-12 h-12 bg-brand-soft text-brand rounded-xl flex items-center justify-center flex-shrink-0">
            <HiOutlineDocumentText className="w-6 h-6" />
          </span>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-ink-primary truncate">
              {doc.title || doc.file_name || getEntity('document').singular}
            </h1>
            <p className="text-sm text-ink-muted truncate">
              {doc.type === 'document'
                ? `${doc.file_name || ''} · ${formatFileSize(doc.file_size)}`
                : doc.type === 'text'
                  ? `${t('documents.types.text')} · ${doc.created_by_name || ''}`
                  : `${t('documents.types.voice')} · ${doc.created_by_name || ''}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {doc.type === 'document' && (
            <>
              <Button
                variant="secondary"
                leadingIcon={HiOutlineEye}
                disabled={!securityAccess.canView}
                onClick={handleViewDocument}
                title={securityAccess.canView ? t('documents.detail.view') : securityAccess.restrictionMessage}
              >
                {t('documents.detail.view')}
              </Button>
              <Button
                variant="secondary"
                leadingIcon={HiOutlineArrowDownTray}
                disabled={!securityAccess.canDownload}
                onClick={handleDownloadDocument}
                title={securityAccess.canDownload ? t('documents.detail.download') : securityAccess.restrictionMessage}
              >
                {t('documents.detail.download')}
              </Button>
            </>
          )}
          {permissions.canEdit && (
            <Button variant="secondary" leadingIcon={HiOutlinePencil} onClick={() => setShowEditModal(true)}>
              {t('common.edit')}
            </Button>
          )}
          {securityAccess.canDelete && (
            <Button
              variant="secondary"
              leadingIcon={HiOutlineTrash}
              onClick={confirmDeleteDocument}
              className="!text-danger hover:!bg-danger-soft hover:!border-danger/40"
            >
              {t('common.delete')}
            </Button>
          )}
        </div>
      </div>

      {!securityAccess.canView && (
        <Banner tone="warning" title={t('documents.detail.limitedAccess')}>
          {securityAccess.restrictionMessage || t('documents.detail.limitedAccessFallback', { entity: getEntity('document').singular.toLowerCase() })}
        </Banner>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card padding="md" className="lg:col-span-2">
          <Card.Header title={t('documents.detail.section.details')} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field icon={HiOutlineDocumentText} label={t('documents.detail.field.type')}>
              <span className="capitalize">{doc.type}</span>
            </Field>
            <Field icon={HiOutlineShieldCheck} label={t('documents.detail.field.securityLevel')}>
              <SecurityBadge level={doc.security_level} />
            </Field>
            {doc.file_name && (
              <Field icon={HiOutlineDocumentText} label={t('documents.detail.field.fileName')}>
                <span className="break-all">{doc.file_name}</span>
              </Field>
            )}
            {doc.file_size != null && (
              <Field icon={HiOutlineCloudArrowUp} label={t('documents.detail.field.fileSize')}>
                {formatFileSize(doc.file_size)}
              </Field>
            )}
            {doc.client_name && (
              <Field icon={HiOutlineUser} label={t('documents.detail.field.client', { entity: getEntity('client').singular })}>
                {doc.client_name}
              </Field>
            )}
            {doc.case_title && (
              <Field icon={HiOutlineFolder} label={t('documents.detail.field.case', { entity: getEntity('case').singular })}>
                {doc.case_title}
              </Field>
            )}
            {doc.session_title && (
              <Field icon={HiOutlineCalendar} label={t('documents.detail.field.session', { entity: getEntity('session').singular })}>
                {doc.session_title}
              </Field>
            )}
            <Field icon={HiOutlineClock} label={t('documents.detail.field.created')}>
              {formatDateTime(doc.created_at)}
            </Field>
            {doc.created_by_name && (
              <Field icon={HiOutlineUser} label={t('documents.detail.field.uploadedBy')}>
                {doc.created_by_name}
              </Field>
            )}
          </div>

          {doc.type === 'text' && doc.content && (
            <div className="mt-6">
              <div className="text-xs font-medium uppercase tracking-wider text-ink-muted mb-1.5">{t('documents.detail.section.content')}</div>
              <Card variant="bordered" padding="md">
                <p className="text-sm text-ink-primary whitespace-pre-wrap break-words">{doc.content}</p>
              </Card>
            </div>
          )}
        </Card>

        <div className="space-y-6">
          <Card padding="md">
            <Card.Header title={t('documents.detail.section.group')} />
            {doc.group_name ? (
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => handleViewGroup(doc.group)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg bg-brand-soft hover:bg-brand-soft/80 text-start transition-colors group"
                >
                  <span className="w-9 h-9 bg-brand text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <HiOutlineFolder className="w-5 h-5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-brand truncate">{doc.group_name}</div>
                    <div className="text-xs text-ink-muted">{t('documents.detail.clickToViewGroup')}</div>
                  </div>
                  <HiOutlineChevronRight className="w-4 h-4 text-brand group-hover:translate-x-0.5 transition-transform rtl:rotate-180 rtl:group-hover:-translate-x-0.5" />
                </button>
                {permissions.canMoveGroups && (
                  <Button variant="secondary" size="sm" fullWidth onClick={() => setShowMoveModal(true)}>
                    {t('documents.detail.moveToAnotherGroup')}
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-ink-muted">{t('documents.detail.noGroup', { entity: getEntity('document').singular.toLowerCase() })}</p>
                {permissions.canMoveGroups && (
                  <Button leadingIcon={HiOutlineFolder} fullWidth onClick={() => setShowMoveModal(true)}>
                    {t('documents.detail.joinAGroup')}
                  </Button>
                )}
              </div>
            )}
          </Card>

          {(doc.type === 'text' || doc.type === 'voice') && (
            <Card padding="md">
              <Card.Header title={t('documents.detail.section.readStatus')} />
              <div className="flex items-center justify-between">
                {/* TODO: needs i18n key for "Status" label */}
                <span className="text-sm text-ink-secondary">Status</span>
                <Badge tone={doc.is_read ? 'success' : 'info'}>
                  {doc.is_read ? t('common.read') : t('common.unread')}
                </Badge>
              </div>
              {doc.read_count > 0 && (
                <p className="text-xs text-ink-muted mt-2">{t('documents.detail.totalReads', { count: doc.read_count })}</p>
              )}
            </Card>
          )}

          {/* Seen-by — visible to the uploader, the workspace owner, or an admin.
              The backend re-checks; this just avoids fetching when clearly N/A. */}
          {(doc.created_by === user?.id || ['owner', 'admin'].includes((userRole || '').toLowerCase())) && (
            <DocumentSeenByList documentId={id} canView />
          )}
        </div>
      </div>

      {/* Comments — any member who can access the document can read/post. */}
      <DocumentComments documentId={id} currentUserId={user?.id} />

      {/* Edit modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={t('documents.detail.editTitle', { entity: getEntity('document').singular.toLowerCase() })}
        size="md"
      >
        <Modal.Body>
          <div className="space-y-4">
            <FormField label={t('documents.detail.titleLabel')}>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder={t('documents.detail.titlePlaceholder', { entity: getEntity('document').singular.toLowerCase() })}
              />
            </FormField>
            <FormField label={t('documents.detail.securityLevelLabel')}>
              <Select
                value={editForm.security_level}
                onChange={(e) => setEditForm(prev => ({ ...prev, security_level: e.target.value }))}
              >
                <option value="green">{t('documents.security.general')}</option>
                <option value="yellow">{t('documents.security.restricted')}</option>
                <option value="red">{t('documents.security.confidential')}</option>
              </Select>
            </FormField>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setShowEditModal(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleUpdateDocument}>{t('common.saveChanges')}</Button>
        </Modal.Footer>
      </Modal>

      {/* Move-to-group modal */}
      <Modal
        isOpen={showMoveModal}
        onClose={() => { setShowMoveModal(false); setMoveGroupSearch('') }}
        title={doc.group ? t('documents.groups.moveToGroup') : t('documents.groups.joinGroup')}
        size="sm"
      >
        <Modal.Body>
          {doc.group && (
            <button
              type="button"
              onClick={() => handleMoveToGroup('ungrouped')}
              className="w-full text-start flex items-center gap-3 px-3 py-2.5 mb-3 rounded-lg border border-danger/30 hover:bg-danger-soft transition-colors"
            >
              <span className="w-7 h-7 bg-danger-soft text-danger rounded-md flex items-center justify-center flex-shrink-0">
                <HiOutlineXMark className="w-3.5 h-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-danger">{t('documents.groups.removeFromGroup')}</div>
                <div className="text-[11px] text-ink-muted">{t('documents.groups.ungroupDescription', { entity: getEntity('document').singular.toLowerCase() })}</div>
              </div>
            </button>
          )}

          {availableGroups.length === 0 ? (
            <EmptyState
              icon={HiOutlineFolder}
              title={t('documents.groups.noGroupsAvailable')}
              description={t('documents.groups.noGroupsHelp')}
              compact
            />
          ) : (
            <>
              {availableGroups.length > 4 && (
                <div className="mb-2">
                  <Input
                    leadingIcon={HiOutlineMagnifyingGlass}
                    size="sm"
                    placeholder={t('documents.groups.searchGroupsPlaceholder')}
                    value={moveGroupSearch}
                    onChange={(e) => setMoveGroupSearch(e.target.value)}
                    autoFocus
                  />
                </div>
              )}
              <div className="max-h-56 overflow-y-auto -mx-1 px-1 space-y-1">
                {availableGroups
                  .filter(g => g.id !== doc.group && g.name.toLowerCase().includes(moveGroupSearch.toLowerCase()))
                  .map(group => (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => handleMoveToGroup(group.id)}
                      className="w-full text-start px-3 py-2 rounded-lg hover:bg-muted transition-colors flex items-center gap-2.5 group/item"
                    >
                      <span className="w-7 h-7 bg-info-soft text-info rounded-md flex items-center justify-center flex-shrink-0">
                        <HiOutlineFolder className="w-3.5 h-3.5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-ink-primary truncate group-hover/item:text-brand transition-colors">{group.name}</div>
                        {group.description && (
                          <div className="text-[11px] text-ink-muted truncate">{group.description}</div>
                        )}
                      </div>
                      <HiOutlineChevronRight className="w-3.5 h-3.5 text-ink-muted flex-shrink-0 rtl:rotate-180" />
                    </button>
                  ))}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => { setShowMoveModal(false); setMoveGroupSearch('') }}>
            {t('common.cancel')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Group detail modal */}
      <Modal
        isOpen={showGroupDetailModal}
        onClose={() => setShowGroupDetailModal(false)}
        title={selectedGroup?.name || t('documents.detail.section.group')}
        size="md"
      >
        <Modal.Body>
          {selectedGroup && (
            <div className="space-y-3">
              {selectedGroup.description && (
                <p className="text-sm text-ink-secondary">{selectedGroup.description}</p>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-ink-muted">{t('documents.detail.documentsInGroup')}</span>
                <Badge tone="info">{selectedGroup.document_count || 0}</Badge>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setShowGroupDetailModal(false)}>{t('common.close')}</Button>
          {selectedGroup && (
            <Button onClick={() => {
              setShowGroupDetailModal(false)
              navigate(`/document-groups/${selectedGroup.id}`)
            }}>
              {t('documents.detail.openGroupPage')}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
      />
    </div>
  )
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wider text-ink-muted mb-1.5 inline-flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      <div className="text-sm text-ink-primary font-medium">{children}</div>
    </div>
  )
}
